<template>
    <Menu as="div" class="relative inline-block text-left">
        <MenuButton
            data-testid="kiosk-actions-menu"
            class="inline-flex h-8 items-center justify-center gap-2 whitespace-nowrap rounded-[6px] border border-gray-300 bg-white px-3 text-xs font-medium leading-8 text-gray-600 transition duration-100 ease-in-out hover:text-gray-800 hover:shadow-button focus:outline-hidden focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        >
            {{ label }}

            <ChevronDownIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
        </MenuButton>

        <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <MenuItems
                class="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-[6px] border border-gray-300 bg-white py-1 shadow-lg focus:outline-hidden"
            >
                <div
                    v-for="(section, sectionIndex) in visibleSections"
                    :key="section.label"
                >
                    <div
                        v-if="sectionIndex > 0"
                        class="my-1 border-t border-gray-200"
                    ></div>

                    <div
                        class="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                    >
                        {{ section.label }}
                    </div>

                    <MenuItem
                        v-for="item in section.items"
                        :key="item.name"
                        v-slot="{ active }"
                    >
                        <button
                            type="button"
                            :data-testid="`kiosk-action-${item.name}`"
                            class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                            :class="[
                                item.danger
                                    ? 'text-topol-red-600'
                                    : 'text-gray-600',
                                active &&
                                    (item.danger
                                        ? 'bg-topol-red-100'
                                        : 'bg-gray-100 text-gray-800'),
                            ]"
                            @click="item.onClick()"
                        >
                            <component
                                :is="item.icon"
                                v-if="item.icon"
                                class="h-5 w-5 shrink-0"
                                :class="
                                    item.danger
                                        ? 'text-topol-red-600'
                                        : 'text-gray-400'
                                "
                            />

                            <span class="truncate">{{ item.name }}</span>
                        </button>
                    </MenuItem>
                </div>
            </MenuItems>
        </transition>
    </Menu>
</template>

<script lang="ts" setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import IKioskActionSection from '@/types/Kiosk/IKioskActionSection';

const props = withDefaults(
    defineProps<{
        sections: IKioskActionSection[];
        label?: string;
    }>(),
    { label: 'Actions' }
);

/**
 * Sections whose items were all filtered out by the caller would otherwise render as a
 * stray heading with nothing under it.
 */
const visibleSections = computed(() =>
    props.sections.filter((section) => section.items.length > 0)
);
</script>
